import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { previewOndInverter } from "../../services/api";
import { ondMessage } from "../../i18n/ondMessages";
import OndConnectionSettings from "./OndConnectionSettings";

const fmt = (value, digits = 0) =>
  Number.isFinite(Number(value)) ? Number(value).toLocaleString("en-GB", { maximumFractionDigits: digits, minimumFractionDigits: digits }) : "—";

// The efficiency curve as the file states it: the interpolated line and the
// file's own points, efficiency against DC input power.
function EfficiencyCurve({ curves, greek }) {
  const lines = (curves || []).filter((c) => (c.points || []).length > 1);
  if (!lines.length) return null;
  const W = 360, H = 150, L = 38, B = 22, T = 8, R = 8;
  const all = lines.flatMap((c) => c.points);
  const xMax = Math.max(...all.map((p) => p.pin_kw));
  const etas = all.map((p) => p.eta_pct).filter((v) => v > 0);
  const yMin = Math.max(80, Math.floor(Math.min(...etas) - 1));
  const yMax = Math.min(100, Math.ceil(Math.max(...etas) + 0.5));
  const x = (v) => L + (v / xMax) * (W - L - R);
  const y = (v) => T + (1 - (Math.max(yMin, v) - yMin) / (yMax - yMin)) * (H - T - B);
  const colours = ["#2563eb", "#15803d", "#b45309"];
  return (
    <Box component="svg" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label={greek ? "Καμπύλη απόδοσης" : "Efficiency curve"}
      sx={{ width: "100%", maxWidth: 420, display: "block", mt: 1, color: "text.secondary" }}>
      <line x1={L} y1={H - B} x2={W - R} y2={H - B} stroke="currentColor" strokeOpacity="0.4" />
      <line x1={L} y1={T} x2={L} y2={H - B} stroke="currentColor" strokeOpacity="0.4" />
      {[yMin, (yMin + yMax) / 2, yMax].map((v) => (
        <text key={v} x={L - 4} y={y(v) + 3} fontSize="9" textAnchor="end" fill="currentColor">{fmt(v, 1)}%</text>
      ))}
      {[0, xMax / 2, xMax].map((v) => (
        <text key={v} x={x(v)} y={H - 6} fontSize="9" textAnchor="middle" fill="currentColor">{fmt(v)} kW</text>
      ))}
      {lines.map((c, i) => (
        <g key={i} stroke={colours[i % colours.length]} fill={colours[i % colours.length]}>
          <polyline fill="none" strokeWidth="1.6"
            points={c.points.map((p) => `${x(p.pin_kw).toFixed(1)},${y(p.eta_pct).toFixed(1)}`).join(" ")} />
          {(c.measured || []).filter((p) => p.pin_kw <= xMax).map((p, j) => (
            <circle key={j} cx={x(p.pin_kw)} cy={y(p.eta_pct)} r="2.4" stroke="none" />
          ))}
        </g>
      ))}
    </Box>
  );
}

export default function OndInverterImport({ value, onChange, accessToken, disabled = false, greek = false }) {
  const input = useRef(null);
  const request = useRef(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => () => { request.current += 1; }, []);
  const tr = (el, en) => (greek ? el : en);

  const selectFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // Allow retrying the same file after a failed upload.
    if (!file) return;
    const id = ++request.current;
    onChange(null);
    setError("");
    setBusy(false);
    if (!/\.ond$/i.test(file.name) || file.size === 0 || file.size > 256 * 1024) {
      setError("Choose a non-empty .ond file no larger than 256 KB.");
      return;
    }
    setBusy(true);
    try {
      const result = await previewOndInverter(file, accessToken);
      if (id === request.current) onChange(result);
    } catch (err) {
      if (id === request.current) setError(err?.message || "The OND file could not be imported.");
    } finally {
      if (id === request.current) setBusy(false);
    }
  };

  const chars = value?.characteristics;
  const model = chars?.ondModel;
  const curves = value?.curves || [];
  return (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {tr("Ανεβάστε το αρχείο OND του αντιστροφέα (από το PVsyst ή τον κατασκευαστή). Η καμπύλη απόδοσης, τα όρια ισχύος και τάσης και τα υπόλοιπα στοιχεία συμπληρώνονται από αυτό.",
          "Upload the inverter's OND file (from PVsyst or the manufacturer). Its efficiency curve, power and voltage limits and the rest of its data are filled in for you.")}
      </Typography>
      <Box>
        <input ref={input} type="file" accept=".ond,.OND" aria-label={tr("Αρχείο OND", "OND file")} hidden
          disabled={disabled || busy} onChange={selectFile} />
        <Button variant="outlined" startIcon={<UploadFileIcon />} disabled={disabled || busy}
          onClick={() => input.current?.click()}>
          {busy ? tr("Ανάγνωση αρχείου OND…", "Reading OND file…")
            : chars ? tr("Αλλαγή αρχείου OND", "Replace OND file") : tr("Επιλογή αρχείου OND", "Choose OND file")}
        </Button>
      </Box>
      {error && <Alert severity="error">{ondMessage(error, greek)}</Alert>}
      {chars && model && (
        <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography fontWeight={600}>{value.inverter_name}</Typography>
            <Chip size="small" label={tr("Καμπύλη απόδοσης OND", "OND efficiency curve")} color="success" variant="outlined" />
          </Stack>
          <Typography variant="body2">
            {chars.Vendor} · {fmt(model.pnom_ac_w / 1000, 1)} kW AC
            {model.pmax_ac_w > model.pnom_ac_w * 1.0001 ? ` (${tr("έως", "up to")} ${fmt(model.pmax_ac_w / 1000, 1)} kW)` : ""}
            {" · "}{chars.Phases === 1 ? tr("μονοφασικός", "single-phase") : tr("τριφασικός", "three-phase")}
          </Typography>
          <Typography variant="body2">
            MPPT {fmt(model.vmpp_min_v)}–{fmt(model.vmpp_max_v)} V · {tr("μέγ.", "max")} {fmt(model.vabs_max_v)} V · {fmt(model.idc_max_a, 1)} A
            {" · "}{chars["Num MPPTs"]} MPPT · {model.n_inputs ?? chars["Num Inputs per MPPT"]} {tr("είσοδοι DC", "DC inputs")}
          </Typography>
          <Typography variant="body2">
            {tr("Απόδοση", "Efficiency")} {tr("μέγ.", "max")} {fmt(model.effic_max_pct, 2)}% · Euro {fmt(model.effic_euro_pct, 2)}%
            {" · "}{tr("κατώφλι", "threshold")} {fmt(model.pthresh_w)} W · {tr("νύχτα", "night")} {fmt(model.night_loss_w)} W
          </Typography>
          <EfficiencyCurve curves={curves} greek={greek} />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, overflowWrap: "anywhere" }}>
            {chars.ondSource?.filename} · {curves.length > 1
              ? tr(`${curves.length} καμπύλες, στα ${curves.map((c) => fmt(c.voltage)).join(" / ")} V`, `${curves.length} curves, at ${curves.map((c) => fmt(c.voltage)).join(" / ")} V`)
              : tr("μία καμπύλη", "one curve")}
          </Typography>
        </Box>
      )}
      {chars && model && <OndConnectionSettings characteristics={chars} greek={greek} disabled={disabled}
        onChange={(ondConnection) => onChange({ ...value, characteristics: { ...chars, ondConnection } })} />}
      {(value?.warnings || []).map((warning) => <Alert key={warning} severity="warning">{ondMessage(warning, greek)}</Alert>)}
      <Typography variant="caption" color="text.secondary">
        {tr("Η προσομοίωση χρησιμοποιεί απευθείας την καμπύλη του αρχείου, και οι απώλειες του αντιστροφέα εμφανίζονται χωριστά (απόδοση, πάνω από την ονομαστική ισχύ, κατώφλι, νύχτα), ώστε να συγκρίνονται με μια μελέτη PVsyst.",
          "The simulation converts on the file's own curve, and the inverter losses are reported separately (efficiency, over nominal power, threshold, night) so they can be compared with a PVsyst study.")}
      </Typography>
    </Stack>
  );
}
